import type { HTMLAttributes, ReactNode } from 'react';
import { clsx } from '../clsx';
import styles from './Segmented.module.css';

export interface SegmentedItem<T extends string> {
  value: T;
  label: ReactNode;
  disabled?: boolean | undefined;
}

export interface SegmentedProps<T extends string> extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  items: ReadonlyArray<SegmentedItem<T>>;
  value: T;
  onChange: (value: T) => void;
  /** Accessible name of the group ("Ansicht"). */
  label: string;
  /** `default`: sand track, white active segment (Liste/Board, Immobilie/Finanzierung); `accent`: sunken track, accent active segment (Beratung 07/09). */
  variant?: 'default' | 'accent' | undefined;
}

/** Two-to-four-way switch of the list headers. */
export function Segmented<T extends string>({
  items,
  value,
  onChange,
  label,
  variant = 'default',
  className,
  ...rest
}: SegmentedProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={clsx(styles.track, variant === 'accent' && styles.accent, className)}
      {...rest}
    >
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={item.disabled}
            className={clsx('pressable', styles.segment, active && styles.active)}
            onClick={() => onChange(item.value)}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
